import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminLayout from "./layouts/AdminLayout";
import Login from "./pages/auth/Login";
import OrganizationProfilePage from "./pages/organization/OrganizationProfilePage";
import BranchesPage from "./pages/organization/BranchesPage";
import DepartmentsPage from "./pages/organization/DepartmentsPage";
import UsersPage from "./pages/admin/UsersPage";
import RolesPage from "./pages/admin/RolesPage";
import PermissionsPage from "./pages/admin/PermissionsPage";
import MenusPage from "./pages/admin/MenusPage";
import SecurityLogs from "./layouts/settings/SecurityLogs";
import ManagementReports from "./layouts/reports/ManagementReports";
import EmployeeReports from "./layouts/reports/EmployeeReports";
import NominalRoll from "./layouts/reports/NominalRoll";
import PayrollSummary from "./layouts/reports/PayrollSummary";
import PayrollDetailed from "./layouts/reports/PayrollDetailed";
import NetPaySchedule from "./layouts/reports/NetPaySchedule";
import Payslips from "./layouts/reports/Payslips";
import TaxReports from "./layouts/reports/TaxReports";
import PensionReports from "./layouts/reports/PensionReports";
import NHFReports from "./layouts/reports/NHFReports";
import NHISReports from "./layouts/reports/NHISReports";
import UnionReports from "./layouts/reports/UnionReports";
import CoopReports from "./layouts/reports/CoopReports";
import routes from "./routes";
// Add import near other variation imports:
import BulkModificationCenter from "layouts/variations/BulkModificationCenter";
import PayrollExceptions from "./layouts/payroll/PayrollExceptions";
import PayrollSnapshots from "layouts/payroll/PayrollSnapshots";

function flattenRoutes(routeList = []) {
  const output = [];

  routeList.forEach((route) => {
    if (route?.route && route?.component) {
      output.push({
        key: route.key || route.route,
        path: route.route,
        element: route.component,
      });
    }

    if (Array.isArray(route?.collapse) && route.collapse.length > 0) {
      output.push(...flattenRoutes(route.collapse));
    }
  });

  return output;
}

const flatRoutes = flattenRoutes(routes);
const PUBLIC_PATHS = ["/authentication/sign-in", "/authentication/sign-up", "/login"];
const protectedRoutes = flatRoutes.filter((route) => !PUBLIC_PATHS.includes(route.path));

export default function AppRoutes() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/authentication/sign-in" element={<Navigate to="/login" replace />} />
        <Route path="/authentication/sign-up" element={<Navigate to="/login" replace />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<AdminLayout />}>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            {protectedRoutes.map((route) => (
              <Route key={route.key} path={route.path} element={route.element} />
            ))}
            {/* Organization Setup pages */}
            <Route
              path="/organization-setup"
              element={<Navigate to="/organization-setup/company-profile" replace />}
            />
            <Route
              path="/organization-setup/company-profile"
              element={<OrganizationProfilePage />}
            />
            <Route path="/organization-setup/branches" element={<BranchesPage />} />
            <Route path="/organization-setup/departments" element={<DepartmentsPage />} />
            {/* SQL/admin seeded menu aliases */}
            <Route
              path="/settings/organization-profile"
              element={<Navigate to="/organization-setup/company-profile" replace />}
            />
            <Route path="/settings/branches" element={<BranchesPage />} />
            <Route path="/settings/departments" element={<DepartmentsPage />} />
            <Route
              path="/settings/org-setup"
              element={<Navigate to="/settings/organization" replace />}
            />
            <Route path="/payroll/periods" element={<Navigate to="/payroll/calendar" replace />} />
            <Route
              path="/payroll/payroll-period"
              element={<Navigate to="/payroll/calendar" replace />}
            />
            <Route path="/payroll/schedule" element={<Navigate to="/payroll/calendar" replace />} />
            {/* Employee aliases */}
            <Route path="/employees" element={<Navigate to="/employee-management" replace />} />
            <Route
              path="/employees/list"
              element={<Navigate to="/hr/employee-management/employeelist" replace />}
            />
            <Route
              path="/employees/staff-list"
              element={<Navigate to="/hr/employee-management/stafflist" replace />}
            />
            <Route
              path="/employees/profile"
              element={<Navigate to="/hr/employees/profile" replace />}
            />
            <Route
              path="/employees/add-edit"
              element={<Navigate to="/hr/employee-management/add-edit" replace />}
            />
            <Route
              path="/employees/bulk-upload"
              element={<Navigate to="/system-setup" replace />}
            />
            <Route
              path="/employees/validation-report"
              element={<Navigate to="/hr/employee-management/stafflist" replace />}
            />
            {/* Payroll masters aliases */}
            <Route path="/payroll-masters" element={<Navigate to="/setup" replace />} />
            <Route path="/masters/pay-items" element={<Navigate to="/setup/pay-items" replace />} />
            <Route path="/payroll/exceptions" element={<PayrollExceptions />} />
            <Route
              path="/payroll/payroll-exceptions"
              element={<Navigate to="/payroll/exceptions" replace />}
            />
            <Route
              path="/payroll/exception-report"
              element={<Navigate to="/payroll/exceptions" replace />}
            />
            <Route
              path="/masters/salary-structures"
              element={<Navigate to="/setup/salary-structures" replace />}
            />
            <Route path="/masters/earnings" element={<Navigate to="/setup/earnings" replace />} />
            <Route
              path="/masters/deductions"
              element={<Navigate to="/setup/deductions" replace />}
            />
            <Route
              path="/masters/designations"
              element={<Navigate to="/setup/designations" replace />}
            />
            <Route path="/masters/cadres" element={<Navigate to="/setup/cadres" replace />} />
            <Route
              path="/masters/grade-levels"
              element={<Navigate to="/setup/grade-levels" replace />}
            />
            <Route
              path="/masters/staff-groups"
              element={<Navigate to="/setup/staff-group" replace />}
            />

            <Route path="/payroll/snapshots" element={<PayrollSnapshots />} />
            {/* Organization Setup aliases */}
            <Route
              path="/organization-setup/overview"
              element={<Navigate to="/organization-setup/company-profile" replace />}
            />
            <Route
              path="/organization-setup/pay-items"
              element={<Navigate to="/setup/pay-items" replace />}
            />
            <Route
              path="/organization-setup/grade-levels"
              element={<Navigate to="/setup/grade-levels" replace />}
            />
            <Route
              path="/organization-setup/cadres"
              element={<Navigate to="/setup/cadres" replace />}
            />
            <Route
              path="/organization-setup/staff-details"
              element={<Navigate to="/setup/staffdetails" replace />}
            />
            <Route
              path="/organization-setup/staff-groups"
              element={<Navigate to="/setup/staff-group" replace />}
            />
            <Route
              path="/organization-setup/designations"
              element={<Navigate to="/setup/designations" replace />}
            />
            <Route
              path="/organization-setup/salary-structures"
              element={<Navigate to="/setup/salary-structures" replace />}
            />
            <Route
              path="/organization-setup/earnings"
              element={<Navigate to="/setup/earnings" replace />}
            />
            <Route
              path="/organization-setup/deductions"
              element={<Navigate to="/setup/deductions" replace />}
            />
            <Route
              path="/organization-setup/non-regular-earnings"
              element={<Navigate to="/setup/nonregularearnings" replace />}
            />
            <Route
              path="/organization-setup/arrears-upload"
              element={<Navigate to="/setup/arrearsupload" replace />}
            />
            <Route
              path="/organization-setup/irregular-allowances"
              element={<Navigate to="/setup/irregularallowances" replace />}
            />
            <Route
              path="/organization-setup/staff-categories"
              element={<Navigate to="/setup/staff-categories" replace />}
            />
            <Route
              path="/organization-setup/map-salary-structures"
              element={<Navigate to="/setup/map-salary-structure" replace />}
            />
            <Route
              path="/organization-setup/designation-details"
              element={<Navigate to="/setup/designation/details" replace />}
            />
            <Route
              path="/organization-setup/cadre-details"
              element={<Navigate to="/setup/cadre/details" replace />}
            />
            {/* Variation aliases */}
            <Route path="/variations" element={<Navigate to="/variations/initiate" replace />} />
            <Route
              path="/variations/dashboard"
              element={<Navigate to="/variations/initiate" replace />}
            />
            <Route
              path="/variations/manual"
              element={<Navigate to="/variations/initiate" replace />}
            />
            <Route
              path="/variations/manual-entry"
              element={<Navigate to="/variations/initiate" replace />}
            />
            <Route
              path="/variations/pending-approvals"
              element={<Navigate to="/variations/review/pending" replace />}
            />
            <Route
              path="/variations/bulk-upload"
              element={<Navigate to="/variations/loaders" replace />}
            />
            <Route
              path="/variations/batches"
              element={<Navigate to="/variations/reports/archive" replace />}
            />
            {/* Recurring component aliases */}
            <Route
              path="/recurring-components"
              element={<Navigate to="/pay-items/allowances" replace />}
            />
            <Route
              path="/recurring/allowances"
              element={<Navigate to="/pay-items/allowances" replace />}
            />
            <Route
              path="/recurring/deductions"
              element={<Navigate to="/pay-items/deductions" replace />}
            />
            <Route path="/recurring/loans" element={<Navigate to="/pay-items/loans" replace />} />
            <Route
              path="/recurring/arrears"
              element={<Navigate to="/pay-items/arrears" replace />}
            />
            <Route
              path="/recurring/stoppages"
              element={<Navigate to="/variations/temporarystoppage" replace />}
            />
            {/* Statutory aliases */}
            <Route
              path="/statutory-configuration"
              element={<Navigate to="/settings/tax-regimes" replace />}
            />
            <Route
              path="/statutory/tax-regimes"
              element={<Navigate to="/settings/tax-regimes" replace />}
            />
            <Route
              path="/statutory/tax-claims"
              element={<Navigate to="/settings/tax-claims-v2" replace />}
            />
            <Route
              path="/statutory/pension-rules"
              element={<Navigate to="/settings/payroll-config" replace />}
            />
            <Route
              path="/statutory/nhf-rules"
              element={<Navigate to="/settings/payroll-config" replace />}
            />
            <Route
              path="/statutory/irregular-allowance-rules"
              element={<Navigate to="/setup/irregularallowances" replace />}
            />
            <Route
              path="/statutory-configuration/pension-rules"
              element={<Navigate to="/settings/payroll-config" replace />}
            />
            <Route
              path="/statutory-configuration/nhf-rules"
              element={<Navigate to="/settings/payroll-config" replace />}
            />
            <Route
              path="/statutory-configuration/irregular-allowance-rules"
              element={<Navigate to="/setup/irregularallowances" replace />}
            />
            {/* Payroll operation aliases */}
            <Route path="/payroll-operations" element={<Navigate to="/payroll" replace />} />
            <Route
              path="/payroll-operations/payroll-run"
              element={<Navigate to="/payroll/run" replace />}
            />
            <Route
              path="/payroll-operations/payroll-preview"
              element={<Navigate to="/payroll/preview" replace />}
            />
            <Route
              path="/payroll-operations/payroll-approval"
              element={<Navigate to="/payroll/approval" replace />}
            />
            <Route
              path="/payroll-operations/payroll-history"
              element={<Navigate to="/payroll/history" replace />}
            />
            <Route
              path="/payroll-operations/payroll-exceptions"
              element={<Navigate to="/payroll/exit-due-review" replace />}
            />
            <Route path="/payroll/periods" element={<Navigate to="/payroll/run" replace />} />
            <Route
              path="/payroll/payroll-period"
              element={<Navigate to="/payroll/run" replace />}
            />
            <Route
              path="/payroll/exceptions"
              element={<Navigate to="/payroll/exit-due-review" replace />}
            />
            <Route
              path="/payroll/payroll-exceptions"
              element={<Navigate to="/payroll/exit-due-review" replace />}
            />
            <Route
              path="/payroll/approval-queue"
              element={<Navigate to="/payroll/approval" replace />}
            />
            {/* Report aliases */}
            <Route path="/reports" element={<Navigate to="/reports/management" replace />} />
            <Route
              path="/reports/payroll"
              element={<Navigate to="/reports/payment/summary" replace />}
            />
            <Route
              path="/reports/statutory"
              element={<Navigate to="/reports/statutory/tax" replace />}
            />
            <Route
              path="/reports/audit"
              element={<Navigate to="/settings/activity-logs" replace />}
            />
            <Route
              path="/reports/bulk-payslips"
              element={<Navigate to="/reports/payslips" replace />}
            />
            <Route
              path="/reports/employee/nominal-roll"
              element={<Navigate to="/reports/nominal-roll" replace />}
            />
            <Route
              path="/reports/employee-reports"
              element={<Navigate to="/reports/employee" replace />}
            />
            {/* Actual Report Pages */}
            <Route path="/reports/management" element={<ManagementReports />} />
            <Route path="/reports/employee" element={<EmployeeReports />} />
            <Route path="/reports/nominal-roll" element={<NominalRoll />} />
            <Route path="/reports/payment/summary" element={<PayrollSummary />} />
            <Route path="/reports/payroll-detail" element={<PayrollDetailed />} />
            <Route path="/reports/net-pay" element={<NetPaySchedule />} />
            <Route path="/reports/payslips" element={<Payslips />} />
            <Route path="/reports/statutory/tax" element={<TaxReports />} />
            <Route path="/reports/statutory/pension" element={<PensionReports />} />
            <Route path="/reports/statutory/nhf" element={<NHFReports />} />
            <Route path="/reports/statutory/nhis" element={<NHISReports />} />
            <Route path="/reports/statutory/union" element={<UnionReports />} />
            <Route path="/reports/statutory/coop" element={<CoopReports />} />
            {/* Optional old aliases */}
            <Route path="/reports/tax" element={<Navigate to="/reports/statutory/tax" replace />} />
            <Route
              path="/reports/pension"
              element={<Navigate to="/reports/statutory/pension" replace />}
            />
            <Route path="/reports/nhf" element={<Navigate to="/reports/statutory/nhf" replace />} />
            <Route
              path="/reports/nhis"
              element={<Navigate to="/reports/statutory/nhis" replace />}
            />
            {/* Admin pages */}
            <Route path="/admin/users" element={<UsersPage />} />
            <Route path="/admin/roles" element={<RolesPage />} />
            <Route path="/admin/permissions" element={<PermissionsPage />} />
            <Route path="/admin/menus" element={<MenusPage />} />
            <Route path="/admin/activity-logs" element={<SecurityLogs />} />
            <Route path="/admin/login-logs" element={<SecurityLogs />} />
            {/* Security aliases */}
            <Route path="/security-admin" element={<Navigate to="/admin/users" replace />} />
            <Route path="/security-admin/users" element={<Navigate to="/admin/users" replace />} />
            <Route path="/security-admin/roles" element={<Navigate to="/admin/roles" replace />} />
            <Route
              path="/security-admin/permissions"
              element={<Navigate to="/admin/permissions" replace />}
            />
            <Route path="/security-admin/menus" element={<Navigate to="/admin/menus" replace />} />
            <Route
              path="/security-admin/security-logs"
              element={<Navigate to="/admin/activity-logs" replace />}
            />
            {/* Support aliases */}
            <Route
              path="/support/contact-support"
              element={<Navigate to="/support/contact" replace />}
            />
            {/* Legacy variation aliases */}
            <Route path="/variation" element={<Navigate to="/variations/initiate" replace />} />
            <Route
              path="/variation/initiate"
              element={<Navigate to="/variations/initiate" replace />}
            />
            <Route
              path="/variation/review"
              element={<Navigate to="/variations/review" replace />}
            />
            <Route
              path="/variation/review/approve"
              element={<Navigate to="/variations/review/approve" replace />}
            />
            <Route
              path="/variation/review/pending"
              element={<Navigate to="/variations/review/pending" replace />}
            />
            <Route
              path="/variation/variationloaders"
              element={<Navigate to="/variations/loaders" replace />}
            />
            <Route
              path="/variation/reports"
              element={<Navigate to="/variations/reports" replace />}
            />
            <Route
              path="/variation/manual/*"
              element={<Navigate to="/variations/initiate" replace />}
            />
            {/* Settings pages */}
            <Route path="/settings/users" element={<UsersPage />} />
            <Route path="/settings/roles" element={<RolesPage />} />
            <Route path="/settings/permissions" element={<PermissionsPage />} />
            <Route path="/settings/menus" element={<MenusPage />} />
            <Route path="/settings/activity-logs" element={<SecurityLogs />} />
            <Route path="/settings/login-logs" element={<SecurityLogs />} />
            <Route
              path="/security-admin/security-logs"
              element={<Navigate to="/settings/security" replace />}
            />
            {/* Bulk Modification Center */}
            <Route path="/variations/bulk-modifications" element={<BulkModificationCenter />} />
            <Route
              path="/variation/bulk-modifications"
              element={<Navigate to="/variations/bulk-modifications" replace />}
            />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Route>
      </Routes>
    </AuthProvider>
  );
}
